import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { UserService } from 'app/core/user/user.service';
// import { User } from 'app/core/user/user.types';
import { EkBreakpointService } from 'app/modules/admin/shared/services/ek-breakpoint.service';
import { EkDensityService } from 'app/modules/admin/shared/services/ek-density.service';
import { User } from 'app/model/user';
import { ToastService } from 'app/services/toast.service';
import { UsersService } from 'app/services/users.service';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        RouterOutlet,
        RouterLink,
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
    ],
    styles: [
        `
            /* ── The rail's own surface ──────────────────────────────────
             * Not bg-card. A white sidebar beside a white header read as one
             * undivided sheet with a hairline drawn across it, which is what
             * "the sidebar looks full white" describes. --ek-surface-rail is a
             * third step, below the page ground, so the chrome sits behind the
             * content rather than level with it.
             *
             * The Fuse nav paints itself with 'background: inherit'
             * (navigation/vertical/styles/appearances/default.scss), so setting
             * it on the host is all that is needed — no ::ng-deep.
             */
            .ek-nav-rail {
                background: var(--ek-surface-rail);
            }

            /* ── Row rhythm ──────────────────────────────────────────────
             * Fuse's default appearance is tuned for a short nav: 40px rows
             * (10px of padding on a 20px line) with 4px of air between them,
             * and another 6px opening and closing every expanded section.
             * This nav is 22 collapsable sections over 113 items, so that
             * rhythm puts most of the list below the fold — opening two
             * sections is enough to make the ones underneath a scroll away.
             *
             * Rows come down to 32px and the gap to 2px, matching the compact
             * density the lists already run at (.ek-density-compact). Only the
             * air changes: the 20px icon, the 13px label and the 16px side
             * padding all stay, so nothing gets harder to read or to hit.
             *
             * These have to outrank Fuse's own rules, which reach the item
             * through four levels of nesting and set the padding SHORTHAND —
             * so a bare .fuse-vertical-navigation-item selector would lose on
             * specificity and silently do nothing. Hence the wrapper class in
             * the middle of each selector; it is load-bearing, not decoration.
             */
            .ek-nav-rail fuse-vertical-navigation-aside-item,
            .ek-nav-rail fuse-vertical-navigation-basic-item,
            .ek-nav-rail fuse-vertical-navigation-collapsable-item {
                margin-bottom: 2px;
            }

            .ek-nav-rail
                .fuse-vertical-navigation-item-wrapper
                .fuse-vertical-navigation-item {
                padding-top: 6px;
                padding-bottom: 6px;
            }

            /* Children sit tighter still. They are a sub-list inside a block
             * that is already indented and already announced by its parent, so
             * they need less separation than the sections do — and there are
             * far more of them, which is where the length actually comes from.
             */
            .ek-nav-rail
                .fuse-vertical-navigation-item-children
                .fuse-vertical-navigation-item-wrapper
                .fuse-vertical-navigation-item {
                padding-top: 5px;
                padding-bottom: 5px;
            }

            /* The cap and tail of an expanded section: 6px each in Fuse, which
             * reads as a gap between a group and its own children. */
            .ek-nav-rail
                fuse-vertical-navigation-collapsable-item
                > .fuse-vertical-navigation-item-children
                > *:first-child {
                margin-top: 2px;
            }

            .ek-nav-rail
                fuse-vertical-navigation-collapsable-item
                > .fuse-vertical-navigation-item-children
                > *:last-child {
                padding-bottom: 2px;
            }

            /* ── Brand block ─────────────────────────────────────────────
             * Replaces the avatar/name/email header that used to open the
             * sidebar. Centred rather than left-aligned: the lockup is a
             * stacked two-line mark, so it reads as a block, and a block
             * hard against the left rail looks dropped rather than placed.
             *
             * Height is pinned to 4rem — the SAME band the page header runs
             * at, the h-16 header row further down this template.
             * Free padding around a 124px lockup made this block ~115px tall,
             * so the rule closing the brand landed most of a header below the
             * header's own bottom edge, and the two pieces of chrome read as
             * unrelated strips rather than one line of console furniture. At
             * 4rem the divider continues the header's edge straight across
             * the rail.
             *
             * The lockup is sized off that band rather than the other way
             * round: 44px of image plus the plate's 4px leaves 8px of air top
             * and bottom. The mark is a stacked wordmark, so it stays legible
             * well below the 69px it used to render at.
             */
            .ek-brand {
                display: flex;
                height: 4rem;
                flex: 0 0 auto;
                align-items: center;
                justify-content: center;
                padding: 0 16px;
                border-bottom: 1px solid var(--ek-border);
            }

            .ek-brand-plate {
                display: block;
                padding: 4px 8px;
                line-height: 0;
                background: #ffffff;
                border-radius: 8px;
                transition: box-shadow 0.15s;
            }

            .ek-brand:hover .ek-brand-plate {
                box-shadow: 0 0 0 1px var(--ek-brand-line);
            }

            .ek-brand-plate img {
                display: block;
                width: auto;
                height: 44px;
            }

            /* Only shown once the rail collapses. */
            .ek-brand-mark {
                display: none;
                width: 34px;
                height: 34px;
            }

            /* ── Collapsed rail ──────────────────────────────────────────
             * Fuse ships a 'compact' appearance for this, and it does not fit:
             * compact.scss carries
             *   > fuse-vertical-navigation-collapsable-item { display: none }
             * because it expects a nav built from ASIDE items. This admin's nav
             * is almost entirely collapsable groups, so compact would render
             * Dashboard and hide the other eleven sections. Restructuring
             * data.ts into aside items is a far bigger change than a width.
             *
             * So this narrows the DEFAULT appearance instead. The width is a
             * Fuse custom property, so setting it on the host is enough — the
             * component's own rules already read it for width/min/max and for
             * the negative margin that slides the rail away.
             */
            .ek-nav-collapsed {
                --fuse-vertical-navigation-width: 76px;
            }

            /* ── Collapsed-rail flyout ───────────────────────────────────
             * Fixed to the viewport and parked just past the rail, so it
             * clears the nav's own overflow. Above the rail in the stack, and
             * above the toolbar, since it is a transient thing the pointer is
             * already on. */
            .ek-nav-flyout {
                position: fixed;
                left: 76px;
                z-index: 60;
                min-width: 200px;
                max-width: 280px;
                padding: 6px;
                /* Longer than the viewport is possible — Reports alone has a
                 * dozen entries across three groups — so the panel scrolls
                 * inside itself rather than off the bottom of the screen.
                 * overscroll-behavior stops that scroll chaining to the page
                 * underneath once it hits the end. */
                overflow-y: auto;
                overscroll-behavior: contain;
                border-radius: 10px;
                border: 1px solid rgba(var(--fuse-border-rgb, 226, 232, 240), 1);
                background-color: rgb(var(--fuse-bg-card-rgb, 255, 255, 255));
                box-shadow:
                    0 2px 4px rgba(15, 23, 42, 0.06),
                    0 12px 28px -12px rgba(15, 23, 42, 0.35);
            }

            /* The item's own name. Doubles as the whole panel for a basic item,
             * which is why it is a heading rather than a caption. */
            .ek-nav-flyout-title {
                padding: 6px 10px;
                font-size: 13px;
                font-weight: 600;
                color: rgba(var(--fuse-text-default-rgb, 30, 41, 59), 1);
                white-space: nowrap;
            }

            .ek-nav-flyout-link {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 7px 10px;
                border-radius: 7px;
                font-size: 13px;
                color: rgba(var(--fuse-text-secondary-rgb, 100, 116, 139), 1);
                text-decoration: none;
                white-space: nowrap;
            }

            /* A nested group's own name. Quieter than the panel heading and
             * not clickable — it has no page of its own, it just says which
             * family the links under it belong to. */
            .ek-nav-flyout-group {
                padding-top: 8px;
                padding-bottom: 4px;
                padding-right: 10px;
                font-size: 10.5px;
                font-weight: 600;
                letter-spacing: 0.08em;
                text-transform: uppercase;
                color: rgba(var(--fuse-text-secondary-rgb, 100, 116, 139), 0.75);
                white-space: nowrap;
            }

            .ek-nav-flyout-link:hover {
                background-color: rgba(var(--fuse-primary-rgb, 59, 130, 246), 0.08);
                color: rgb(var(--fuse-primary-rgb, 59, 130, 246));
            }

            .ek-nav-collapsed .ek-brand {
                padding: 0 8px;
            }

            .ek-nav-collapsed .ek-brand-plate {
                display: none;
            }

            .ek-nav-collapsed .ek-brand-mark {
                display: block;
            }

            /* The label, the collapsable arrow and any badge have nowhere to go
             * at 76px. !important is required rather than tidy: tailwind runs
             * with important:true, so the @apply rules these fight are already
             * !important. */
            .ek-nav-collapsed .fuse-vertical-navigation-item-title-wrapper,
            .ek-nav-collapsed .fuse-vertical-navigation-item-arrow,
            .ek-nav-collapsed .fuse-vertical-navigation-item-badge {
                display: none !important;
            }

            /* Children stay mounted (Fuse tracks their open state) but must not
             * take space — otherwise an already-open group leaves a stack of
             * blank rows down the collapsed rail. */
            .ek-nav-collapsed .fuse-vertical-navigation-item-children {
                display: none !important;
            }

            .ek-nav-collapsed .fuse-vertical-navigation-item {
                justify-content: center !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
            }

            .ek-nav-collapsed .fuse-vertical-navigation-item-icon {
                margin-right: 0 !important;
            }

            /* Group headings are words only — nothing to show at this width. */
            .ek-nav-collapsed fuse-vertical-navigation-group-item {
                display: none !important;
            }

            /* ── Header identity chip ────────────────────────────────── */
            /*
             * The chip is drawn at REST, not only on hover.
             *
             * It started transparent until hovered, which made the one control
             * on the toolbar look like plain text: nothing said it opened a
             * menu until the pointer happened to cross it, and on a touch
             * screen there is no hover at all, so it never appeared. Hover now
             * deepens an existing chip instead of conjuring one.
             */
            .ek-user-chip {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                height: 42px;
                padding: 0 10px 0 6px;
                border: 1px solid var(--ek-brand-line);
                border-radius: 999px;
                background: var(--ek-brand-soft);
                cursor: pointer;
                transition:
                    background 0.15s,
                    border-color 0.15s;
            }

            .ek-user-chip:hover,
            .ek-user-chip[aria-expanded='true'] {
                background: var(--ek-brand-soft-strong);
            }

            .ek-user-chip-avatar {
                width: 32px;
                height: 32px;
                border-radius: 999px;
                object-fit: cover;
            }

            .ek-user-chip-who {
                flex-direction: column;
                align-items: flex-start;
                min-width: 0;
                line-height: 1.2;
            }

            .ek-user-chip-name {
                font-size: 13px;
                font-weight: 600;
                max-width: 15ch;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .ek-user-chip-mail {
                font-size: 11px;
                color: var(--ek-ink-secondary);
                max-width: 20ch;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .ek-user-chip-chev {
                color: var(--ek-ink-hint);
            }

            .user-profile-menu {
                min-width: 250px;
            }

            .mat-mdc-menu-panel.user-profile-menu {
                border-radius: 8px;
                box-shadow:
                    0 4px 6px -1px rgba(0, 0, 0, 0.1),
                    0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }

            .mat-mdc-menu-item {
                display: flex;
                align-items: center;
                gap: 8px;
                height: 48px;
            }

            .mat-mdc-menu-item mat-icon {
                margin-right: 8px;
            }
        `,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    /** Desktop only — see toggleNavigation(). Below md the nav is a drawer. */
    isNavCollapsed = false;
    navigation: Navigation;
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    imageBaseUrl = environment.imageUrl;

    /**
     * Injected for its side effect, not for the template.
     *
     * The toolbar toggle is gone — rows are compact everywhere now — but the
     * service is what puts `.ek-density-compact` on <body>, and a
     * providedIn:'root' service is only constructed when something asks for it.
     * Drop this line and every list silently reverts to comfortable spacing.
     */
    readonly density = inject(EkDensityService);
    readonly breakpoints = inject(EkBreakpointService);

    // Password dialog properties
    passwordForm: FormGroup;
    isChangingPassword: boolean = false;
    hideCurrentPassword: boolean = true;
    hideNewPassword: boolean = true;
    hideConfirmPassword: boolean = true;
    passwordDialogRef: MatDialogRef<any>;

    // Template references
    @ViewChild('changePasswordDialog')
    changePasswordDialogRef: TemplateRef<any>;

    /**
     * Constructor
     */
    constructor(
        private router: Router,
        private _navigationService: NavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _userService: UserService,
        private _usersService: UsersService,
        private _toastService: ToastService,
        private _formBuilder: FormBuilder,
        private _dialog: MatDialog,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService
    ) {
        // Initialize password form
        this.passwordForm = this._formBuilder.group(
            {
                currentPassword: ['', [Validators.required]],
                newPassword: [
                    '',
                    [Validators.required, Validators.minLength(6)],
                ],
                confirmPassword: ['', [Validators.required]],
            },
            {
                validators: this.passwordMatchValidator,
            }
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service

        this.user = this._userService.getCurrentUser();
        // this._userService.user$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((user: User) => {
        //         this.user = user;
        //     });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    /**
     * The toolbar toggle does two different things by width, on purpose.
     *
     * On a phone the nav is `over` — a drawer on top of the page — so toggling
     * it open and shut is the only sensible behaviour, and that is what this
     * always used to do.
     *
     * On a desktop it is `side`, in the layout flow, and closing it outright
     * threw away every wayfinding cue on the screen: no icons, no indication of
     * where you were, and the only way back was to find the same button again.
     * There it collapses to an icon rail instead, so the page gets its width
     * back while the navigation stays on screen.
     */
    toggleNavigation(name: string): void {
        if (!this.isScreenSmall) {
            this.isNavCollapsed = !this.isNavCollapsed;
            return;
        }

        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            navigation.toggle();
        }
    }

    /**
     * Clicking a GROUP while collapsed opens the rail rather than the group.
     *
     * A collapsable item's children are hidden at 76px — there is nowhere to
     * put them — so letting the click through would toggle something invisible
     * and look like the icon did nothing. Expanding first means the click lands
     * where the user can see the result, and the group opens on the second one.
     *
     * Basic items (Dashboard) are untouched: they navigate, which works at any
     * width, so the rail stays collapsed and the page just changes.
     */
    // ── Collapsed-rail flyout ───────────────────────────────────────────────
    // A 76px rail shows icons and nothing else, so the only way to learn what
    // one is was to click it and watch the whole rail expand. Hovering now
    // names it, and names its children when it has any — which is also the
    // only way to reach a sub-page without expanding the rail first.

    flyout: {
        title: string;
        top: number;
        maxHeight: number;
        children: FuseNavigationItem[];
    } | null = null;
    private _flyoutTimer?: ReturnType<typeof setTimeout>;

    /**
     * Roughly how tall the panel will be, from the data rather than the DOM.
     *
     * Measuring would mean rendering it at the wrong place first and moving it,
     * which is a visible jump. The numbers are the rendered heights of a link
     * and a group heading; being a few pixels out only shifts where a long
     * panel starts, and max-height keeps it on screen either way.
     */
    private _flyoutHeight(items: FuseNavigationItem[]): number {
        return items.reduce(
            (total, item) =>
                total +
                (item.children?.length
                    ? 26 + this._flyoutHeight(item.children)
                    : 33),
            0
        );
    }

    /**
     * Delegated from the nav host: Fuse renders the items, so there is nothing
     * of ours to bind a handler to. The hovered item is matched back to the
     * navigation data by its title — the titles the rail hides are still in the
     * DOM, and top-level titles are unique.
     */
    onNavHover(event: MouseEvent): void {
        if (!this.isNavCollapsed || this.isScreenSmall) {
            return;
        }

        const target = event.target as HTMLElement | null;
        const host = target?.closest(
            'fuse-vertical-navigation-basic-item, fuse-vertical-navigation-collapsable-item'
        ) as HTMLElement | null;

        // Between items — leave whatever is open alone rather than flickering.
        if (!host) {
            return;
        }

        const title =
            host
                .querySelector('.fuse-vertical-navigation-item-title span')
                ?.textContent?.trim() ?? '';
        if (!title || title === this.flyout?.title) {
            clearTimeout(this._flyoutTimer);
            return;
        }

        const item = (this.navigation?.default ?? []).find(
            (nav) => nav.title === title
        );
        const children = item?.children ?? [];

        // Anchored to the item, then pulled back up so it cannot run off the
        // bottom — Reports has enough children to reach past the viewport, and
        // a panel whose last half is unreachable is worse than one that starts
        // higher than the icon it belongs to.
        const margin = 12;
        const maxHeight = window.innerHeight - margin * 2;
        const height = Math.min(44 + this._flyoutHeight(children), maxHeight);
        const anchor = host.getBoundingClientRect().top;
        const top = Math.max(
            margin,
            Math.min(anchor, window.innerHeight - margin - height)
        );

        clearTimeout(this._flyoutTimer);
        this.flyout = { title, top, maxHeight, children };
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Closed on a delay, because the pointer has to cross the gap between the
     * rail and the panel to reach a child. keepFlyout cancels it on arrival.
     */
    closeFlyout(): void {
        clearTimeout(this._flyoutTimer);
        this._flyoutTimer = setTimeout(() => {
            this.flyout = null;
            this._changeDetectorRef.markForCheck();
        }, 140);
    }

    keepFlyout(): void {
        clearTimeout(this._flyoutTimer);
    }

    onNavClick(event: MouseEvent): void {
        if (!this.isNavCollapsed || this.isScreenSmall) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (target?.closest('fuse-vertical-navigation-collapsable-item')) {
            // Swallowed, not acted on. Clicking a group used to expand the
            // whole rail, which is a big move to answer a small question —
            // hovering now names the group and lists its pages, so the click
            // has nothing left to do. Fuse's own toggle is stopped too: it
            // would open children the rail hides, so the only visible effect
            // would be an arrow turning.
            event.stopPropagation();
            event.preventDefault();
        }
    }

    public logout(): void {
        localStorage.clear();
        this._changeDetectorRef.detectChanges();
        this.router.navigate(['/sign-out']);
    }

    /**
     * Open change password dialog
     */
    public navigateToChangePassword(): void {
        // Open the dialog using the template reference
        this.passwordDialogRef = this._dialog.open(
            this.changePasswordDialogRef,
            {
                width: '400px',
                disableClose: false,
            }
        );
    }

    /**
     * Password match validator
     */
    passwordMatchValidator(
        formGroup: FormGroup
    ): { [key: string]: boolean } | null {
        const newPassword = formGroup.get('newPassword')?.value;
        const confirmPassword = formGroup.get('confirmPassword')?.value;

        if (newPassword !== confirmPassword) {
            formGroup
                .get('confirmPassword')
                ?.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        } else {
            formGroup.get('confirmPassword')?.setErrors(null);
            return null;
        }
    }

    /**
     * Close password dialog
     */
    closePasswordDialog(): void {
        this.passwordDialogRef.close();
        this.passwordForm.reset();
        this.isChangingPassword = false;
    }

    /**
     * Change password
     */
    async changePassword(): Promise<void> {
        if (this.passwordForm.invalid) {
            return;
        }

        this.isChangingPassword = true;

        try {
            const request = {
                userId: this.user.id,
                currentPassword: this.passwordForm.value.currentPassword,
                newPassword: this.passwordForm.value.newPassword,
                confirmPassword: this.passwordForm.value.confirmPassword,
            };

            const token = localStorage.getItem('SessionToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            // Call the API to update the password
            await this._usersService.updateAdminProfile(request);

            this._toastService.success('Password changed successfully');
            this.closePasswordDialog();
        } catch (error) {
            console.error('Error changing password:', error);
            this._toastService.error(
                'Failed to change password. Please try again.'
            );
        } finally {
            this.isChangingPassword = false;
            this._changeDetectorRef.markForCheck();
        }
    }
}
